import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Breadcrumb from 'src/components/Breadcrumb';
import SearchBar from 'src/components/SearchBar';
import DownloadButton from 'src/components/DownloadButton';
import useDebounce from 'src/hooks/useDebounce';
import {
  loadAgingBalance,
  downloadExcelAgingBalance,
  CLEAR_AGING_BALANCE_OPTIONS,
  downloadExcelAgingBalanceResume
} from 'src/store/actions/accountingActions';
import {
  selectAgingBalanceQueryOptions,
  selectAgingBalanceResumeQueryOptions
} from 'src/store/selectors/accountingSelector';
import Filter from 'src/components/Filter/Filter';
import { FilterList } from '@mui/icons-material';
import clsx from 'clsx';
import FILTER_TYPES from 'src/utils/constants/filter';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import useStyles from './styles';

const filters = [
  {
    title: 'COMMON.DATE',
    controlGroup: FILTER_TYPES.DATE,
    items: [
      {
        label: 'COMMON.DATE',
        value: 'date',
        view: ['year', 'month', 'day']
      }
    ]
  }
];

function toEndOfDay(date) {
  return momentLocalTimezone(date)
    .endOf('day')
    .toDate();
}

function getFilterOptions(options) {
  let date = null;

  if (options.date) {
    date = toEndOfDay(options.date);
  }

  return { date };
}

const Toolbar = ({ className, tabIndex = 0, ...rest }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const queryOpts = useSelector(selectAgingBalanceQueryOptions);
  const resumeQueryOpts = useSelector(selectAgingBalanceResumeQueryOptions);
  const [searchValue, setSearchValue] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterValues, setFilterValues] = useState({});

  const EXPORT_MAP = {
    0: { options: queryOpts, action: downloadExcelAgingBalance },
    1: { options: queryOpts, action: downloadExcelAgingBalance },
    2: { options: resumeQueryOpts, action: downloadExcelAgingBalanceResume }
  };

  const handleClickFilter = useCallback(
    event => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const debounceSearch = useDebounce(
    (name, queryOptions) =>
      dispatch(loadAgingBalance({ ...queryOptions, name })),
    400
  );

  const handleFilterChange = useCallback(
    event => {
      setFilterValues(event);
      const filterOptions = getFilterOptions(event);
      debounceSearch(searchValue, {
        ...queryOpts,
        page: 0,
        ...filterOptions
      });
    },
    [setFilterValues, searchValue, debounceSearch, queryOpts, getFilterOptions]
  );

  const handleCloseFilter = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleSearch = useCallback(
    e => {
      const { value } = e.target;
      setSearchValue(value);
      debounceSearch(value, { ...queryOpts, page: 0 });
    },
    [setSearchValue, debounceSearch, queryOpts]
  );

  const handleExcelExport = useCallback(() => {
    dispatch(EXPORT_MAP[tabIndex].action(EXPORT_MAP[tabIndex].options));
  }, [dispatch, tabIndex]);

  useEffect(() => {
    const initialDate = toEndOfDay(queryOpts.date || new Date());
    dispatch(
      loadAgingBalance({
        ...queryOpts,
        date: initialDate
      })
    );

    return () => dispatch({ type: CLEAR_AGING_BALANCE_OPTIONS });
  }, [dispatch]);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column' }} {...rest}>
        <Breadcrumb doNotRedirectIndexList={[0]} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { lg: 'row', sm: 'row', xs: 'column' }
          }}
        >
          <Typography sx={{ fontWeight: '400' }} color="primary" variant="h1">
            {t('NAVIGATION.AGING_BALANCE')}
          </Typography>
          <Box className={classes.actionsContainer}>
            {tabIndex !== 2 && (
              <>
                <SearchBar
                  handleSearch={handleSearch}
                  searchValue={searchValue}
                  placeholder={t('CLIENTS_VIEW.SEARCH_BY_NAME')}
                />

                <Filter
                  anchorEl={anchorEl}
                  filterValues={filterValues}
                  handleClose={handleCloseFilter}
                  handleChange={handleFilterChange}
                  filters={filters}
                />
                <Button
                  className={clsx(
                    classes.buttons,
                    classes.horizontalPadding,
                    classes.outlined
                  )}
                  sx={{ backgroundColor: 'white', padding: '10px 20px' }}
                  color="primary"
                  onClick={handleClickFilter}
                >
                  <FilterList className={classes.icon} />
                  {t('COMMON.FILTER')}
                </Button>
              </>
            )}
            <DownloadButton
              onDownload={handleExcelExport}
              title={t('COMMON.DOWNLOAD')}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

Toolbar.propTypes = {
  className: PropTypes.string,
  tabIndex: PropTypes.number
};

export default Toolbar;
